import { type Icon, Icons } from '../icons';
import { isNumber } from '@blurb/utils';
import clsx from 'clsx';
import React from 'react';

export interface LoaderProps {
  /** Loader variant.
   *
   * **DEFAULT:** `bars`
   */
  variant?: 'dots' | 'puff' | 'bars';
  /**
   * Mounts if `true`.
   *
   * **DEFAULT:** `true`
   */
  visible?: boolean;
  /** If `true` the loader has a different color. */
  isError?: boolean;
  /**
   * Sets the width and height of the loader.
   *
   * **DEFAULT:** `sm`
   */
  size?: 'sm' | 'md' | 'lg' | 'xl' | number;
}

/**
 * Loader component. Indicates loading or error state.
 *
 * Color of the loader is change by setting the text color.
 */
export const Loader: React.FC<
  Omit<React.ComponentProps<Icon>, 'color' | keyof LoaderProps> & LoaderProps
> = ({
  variant = 'bars',
  visible = true,
  isError,
  size = 'sm',
  width,
  height,
  className,
  ...props
}) => {
  const dimensions = React.useMemo(() => {
    if (width || height) {
      return { width, height };
    }

    if (isNumber(size)) {
      return { width: size, height: size };
    }

    switch (size) {
      case 'xl':
        return { width: 48, height: 48 };

      case 'lg':
        return { width: 32, height: 32 };

      case 'md':
        return { width: 24, height: 24 };

      case 'sm':
      default:
        return { width: 16, height: 16 };
    }
  }, [size, width, height]);

  const Icon = React.useMemo(() => {
    switch (variant) {
      case 'dots':
        return Icons.DotsLoader;
      case 'puff':
        return Icons.PuffLoader;
      case 'bars':
      default:
        return Icons.BarsLoader;
    }
  }, [variant]);

  if (!visible) return <></>;

  return (
    <Icon
      width={dimensions.width}
      height={dimensions.height}
      className={clsx(className, 'text-current block', {
        'text-spark': !!isError,
      })}
      {...props}
    />
  );
};
